// src/poker/pages/PokerLobbyPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPokerSocket,
  pokerCreateRoom,
  pokerJoinRoom,
  pokerLeaveRoom,
  pokerPing,
  pokerStartGame,
} from "../ws/pokerClient";

// 5 ogólnych presetów turniejowych (single-table)
const TOURNEY_PRESETS = [
  { key: "home", name: "Home (zbalansowany)", targetDurationMin: 150, levelMinutes: 10, startingStackBB: 200 },
  { key: "turbo", name: "Turbo", targetDurationMin: 120, levelMinutes: 8, startingStackBB: 100 },
  { key: "regular", name: "Regular", targetDurationMin: 180, levelMinutes: 12, startingStackBB: 150 },
  { key: "deep", name: "Deep", targetDurationMin: 240, levelMinutes: 15, startingStackBB: 250 },
  { key: "hyper", name: "Hyper", targetDurationMin: 75, levelMinutes: 5, startingStackBB: 70 },
];

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function targetAvgEndBB(playersPlanned) {
  const p = clamp(Number(playersPlanned) || 8, 2, 10);
  return clamp(30 - p * 1.5, 18, 28);
}

function niceRound(n) {
  const x = Math.max(1, Number(n) || 1);
  if (x < 20) return Math.round(x);
  if (x < 100) return Math.round(x / 5) * 5;
  if (x < 500) return Math.round(x / 10) * 10;
  if (x < 2000) return Math.round(x / 25) * 25;
  if (x < 10000) return Math.round(x / 50) * 50;
  return Math.round(x / 100) * 100;
}

function buildPreviewLevels({ sb0, bb0, levelMinutes, levelsCount, startingStackBB, plannedPlayers }) {
  const s0 = Math.max(1, Math.trunc(Number(sb0) || 1));
  const b0 = Math.max(s0, Math.trunc(Number(bb0) || 2));
  const min = Math.max(1, Math.trunc(Number(levelMinutes) || 10));
  const n = clamp(Math.trunc(Number(levelsCount) || 16), 4, 60);

  const avgEnd = targetAvgEndBB(plannedPlayers);
  const startingStackChips = Math.max(1, Math.trunc(Number(startingStackBB) || 100)) * b0;

  const bbEndTarget = Math.max(b0, startingStackChips / avgEnd);
  const steps = Math.max(1, n - 1);
  const ratio = Math.pow(bbEndTarget / b0, 1 / steps);

  const sbRatio = s0 / b0;

  const out = [];
  for (let i = 0; i < n; i++) {
    const rawBB = b0 * Math.pow(ratio, i);
    const bb = niceRound(rawBB);
    const sb = Math.max(1, Math.min(bb - 1, niceRound(bb * sbRatio)));
    out.push({ level: i + 1, sb, bb, minutes: min });
  }
  return { levels: out, avgEndBB: avgEnd, bbEndTarget: niceRound(bbEndTarget) };
}

export default function PokerLobbyPage() {
  const socket = useMemo(() => getPokerSocket(), []);
  const nav = useNavigate();

  const [ping, setPing] = useState(null);

  const [createTitle, setCreateTitle] = useState("Stół testowy");

  // CASH vs TOURNAMENT
  const [mode, setMode] = useState("cash"); // "cash" | "tournament"

  // blinds (cash: editable; tournament: fixed for now)
  const [bb, setBb] = useState(2);
  const sb = useMemo(() => Math.max(1, Math.floor(Number(bb || 2) / 2)), [bb]);

  // starting stack in BB
  const [startingStackBB, setStartingStackBB] = useState(100);

  // tournament settings
  const [tPresetKey, setTPresetKey] = useState("home");
  const [targetDurationMin, setTargetDurationMin] = useState(150);
  const [levelMinutes, setLevelMinutes] = useState(10);

  // room params
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [decisionTimeSec, setDecisionTimeSec] = useState(30);

  const [showStructure, setShowStructure] = useState(true);

  // join/create
  const [code, setCode] = useState("");
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    pokerPing().then((res) => mounted && setPing(res));
    return () => {
      mounted = false;
    };
  }, []);

  // defaults per mode
  useEffect(() => {
    if (mode === "cash") {
      setBb(2);
      setStartingStackBB(100);
      setMaxPlayers(8);
    } else {
      // turniej: na razie sztywno start 25/50
      setBb(50);

      const p = TOURNEY_PRESETS.find((x) => x.key === tPresetKey) || TOURNEY_PRESETS[0];
      setTargetDurationMin(p.targetDurationMin);
      setLevelMinutes(p.levelMinutes);
      setStartingStackBB(p.startingStackBB);

      // single-table: max 8
      setMaxPlayers((v) => clamp(v || 8, 2, 8));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // preset -> ustawia parametry (ale można je ręcznie zmienić po wyborze)
  useEffect(() => {
    if (mode !== "tournament") return;
    const p = TOURNEY_PRESETS.find((x) => x.key === tPresetKey);
    if (!p) return;
    setTargetDurationMin(p.targetDurationMin);
    setLevelMinutes(p.levelMinutes);
    setStartingStackBB(p.startingStackBB);
  }, [tPresetKey, mode]);

  // clamp maxPlayers for tournament
  useEffect(() => {
    if (mode !== "tournament") return;
    if (Number(maxPlayers) > 8) setMaxPlayers(8);
  }, [maxPlayers, mode]);

  useEffect(() => {
    const onRoomState = (payload) => {
      if (!payload?.ok) return;
      setRoom(payload.room);
    };

    socket.on("POKER:ROOM_STATE", onRoomState);
    return () => {
      socket.off("POKER:ROOM_STATE", onRoomState);
    };
  }, [socket]);

  const levelsCount = useMemo(() => {
    if (mode !== "tournament") return 0;
    const t = Math.max(1, Math.trunc(Number(targetDurationMin) || 150));
    const m = Math.max(1, Math.trunc(Number(levelMinutes) || 10));
    return clamp(Math.ceil(t / m), 4, 60);
  }, [mode, targetDurationMin, levelMinutes]);

  const preview = useMemo(() => {
    if (mode !== "tournament") return null;
    return buildPreviewLevels({
      sb0: sb,
      bb0: bb,
      levelMinutes,
      levelsCount,
      startingStackBB,
      plannedPlayers: maxPlayers,
    });
  }, [mode, sb, bb, levelMinutes, levelsCount, startingStackBB, maxPlayers]);

  const isHost =
    !!ping?.user?._id && !!room?.hostUserId && String(ping.user._id) === String(room.hostUserId);

  const stackChips = Math.max(1, Math.trunc(Number(bb) || 1)) * Math.max(1, Math.trunc(Number(startingStackBB) || 1));

  const onCreate = async () => {
    setErr("");

    const payload = {
      title: createTitle,
      maxPlayers: mode === "tournament" ? clamp(maxPlayers, 2, 8) : clamp(maxPlayers, 2, 10),
      decisionTimeSec,
      mode,
      sb,
      bb,
      startingStackBB,
      tournament:
        mode === "tournament"
          ? {
              presetKey: tPresetKey,
              targetDurationMin: Math.max(30, Math.trunc(Number(targetDurationMin) || 150)),
              levelDurationSec: Math.max(60, Math.trunc(Number(levelMinutes) || 10) * 60),
              plannedPlayers: clamp(Math.trunc(Number(maxPlayers) || 8), 2, 8),

              // ✅ TO jest klucz: finalna struktura idzie na serwer
              levels: preview?.levels || [],
            }
          : null,
    };

    const res = await pokerCreateRoom(payload);
    if (!res?.ok) {
      setErr(`Nie udało się utworzyć stołu: ${res?.error || "unknown"}`);
      return;
    }

    const joinRes = await pokerJoinRoom(res.code);
    if (!joinRes?.ok) {
      setErr(`Stół utworzony, ale nie udało się dołączyć: ${joinRes?.error || "unknown"}`);
      return;
    }

    setRoom(joinRes.room);
    setCode(res.code);
  };

  const onJoin = async () => {
    setErr("");
    const c = String(code || "").trim().toUpperCase();
    if (!c) {
      setErr("Wpisz kod stołu.");
      return;
    }

    const res = await pokerJoinRoom(c);
    if (!res?.ok) {
      setErr(`Nie udało się dołączyć: ${res?.error || "unknown"}`);
      return;
    }

    setRoom(res.room);
    if (res.room?.status === "running") nav(`/pokergame/${c}`);
  };

  const onLeave = async () => {
    setErr("");
    await pokerLeaveRoom();
    setRoom(null);
  };

  const onStartGame = async () => {
    setErr("");
    const c = room?.code;
    const res = await pokerStartGame(c);
    if (!res?.ok) {
      setErr(`Nie udało się wystartować gry: ${res?.error || "unknown"}`);
      return;
    }
    if (res?.room) setRoom(res.room);
    nav(`/pokergame/${c}`);
  };

  // ===== UI helpers (spójne z inputami w ActionPanel) =====
  const inputCls = "input-secondary";

  return (
    <div style={{ padding: 18, minHeight: "calc(100vh - 36px)", display: "flex", justifyContent: "center" }}>
      <div className="glass-container" style={{ width: "100%", maxWidth: 980 }}>
        <h1 style={{ marginTop: 0 }}>Poker – lobby</h1>

        {/* Join line on top */}
        {!room ? (
          <div className="panel-card" style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Dołącz kodem</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input
                className={inputCls}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Kod stołu, np. A7K9Q2"
                style={{ flex: "1 1 240px", minWidth: 220 }}
              />
              <button className="btn btn-secondary" type="button" onClick={onJoin} style={{ whiteSpace: "nowrap" }}>
                Dołącz
              </button>
            </div>
          </div>
        ) : null}

        {/* Status */}
        <div className="panel-card" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Status połączenia</div>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Socket: <b>{socket.connected ? "połączony" : "łączenie..."}</b>
            {" · "}
            Auth:{" "}
            <b>
              {ping?.ok
                ? ping?.user
                  ? `zalogowany (${ping.user.email || "user"})`
                  : "niezalogowany"
                : "sprawdzam..."}
            </b>
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
            Jeśli tu jest „niezalogowany”, to cookie sesji nie dochodzi do backendu (CORS/withCredentials).
          </div>
        </div>

        {!room ? (
          <div className="panel-card">
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Utwórz stół (admin)</div>

            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Nazwa</div>
                <input className={inputCls} value={createTitle} onChange={(e) => setCreateTitle(e.target.value)} />
              </div>

              {/* Mode + stack */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Tryb</div>
                  <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="cash">Cash (stałe blindy)</option>
                    <option value="tournament">Turniej (rosnące blindy)</option>
                  </select>
                </div>

                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Stack startowy (BB)</div>
                  <input
                    className={inputCls}
                    type="number"
                    min={10}
                    max={9999}
                    value={startingStackBB}
                    onChange={(e) => setStartingStackBB(Number(e.target.value))}
                  />
                </div>
              </div>

              {/* Blinds */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
                    SB {mode === "tournament" ? "(auto = 1/2 BB)" : ""}
                  </div>
                  <input className={inputCls} type="number" value={sb} disabled style={{ opacity: 0.85 }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
                    BB {mode === "tournament" ? "(Level 1, stałe)" : ""}
                  </div>
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={bb}
                    onChange={(e) => setBb(Number(e.target.value))}
                    disabled={mode === "tournament"}
                    style={mode === "tournament" ? { opacity: 0.85 } : undefined}
                  />
                </div>
              </div>

              <div style={{ fontSize: 12, opacity: 0.8 }}>
                Stack w żetonach: <b>{stackChips}</b>{" "}
                <span style={{ opacity: 0.7 }}>
                  ({startingStackBB} BB przy BB={Math.max(1, bb)})
                </span>
              </div>

              {/* Tournament block */}
              {mode === "tournament" ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Preset turnieju</div>
                      <select className={inputCls} value={tPresetKey} onChange={(e) => setTPresetKey(e.target.value)}>
                        {TOURNEY_PRESETS.map((p) => (
                          <option key={p.key} value={p.key}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Te 2 pola zostają, bo czas turnieju i min/level to sensowna ręczna regulacja.
                        Jeśli chcesz "tylko preset", to po prostu je zablokujemy (disabled) w następnym kroku. */}
                    <div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Czas turnieju (min)</div>
                      <input
                        className={inputCls}
                        type="number"
                        min={30}
                        max={600}
                        value={targetDurationMin}
                        onChange={(e) => setTargetDurationMin(Number(e.target.value))}
                      />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Minuty / level</div>
                      <input
                        className={inputCls}
                        type="number"
                        min={1}
                        max={60}
                        value={levelMinutes}
                        onChange={(e) => setLevelMinutes(Number(e.target.value))}
                      />
                    </div>
                  </div>

                  <div style={{ fontSize: 12, opacity: 0.8, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div>
                      Wyjdzie poziomów: <b>{levelsCount}</b>
                    </div>
                    <div>
                      Target końca (avg stack): <b>{preview?.avgEndBB} BB</b>{" "}
                      <span style={{ opacity: 0.65 }}>(dla {maxPlayers} graczy)</span>
                    </div>
                    <div>
                      Szac. BB na ostatnim: <b>{preview?.bbEndTarget}</b>
                    </div>
                    {Number(maxPlayers) > 8 ? (
                      <div style={{ color: "var(--color-accent)" }}>
                        Single-table: na razie max 8 graczy.
                      </div>
                    ) : null}
                  </div>

                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => setShowStructure((v) => !v)}
                    style={{ justifySelf: "start" }}
                  >
                    {showStructure ? "Ukryj podgląd struktury" : "Pokaż podgląd struktury"}
                  </button>

                  {showStructure && preview?.levels?.length ? (
                    <div style={{ marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 10 }}>
                      <div style={{ fontWeight: 800, marginBottom: 8 }}>Podgląd struktury (orientacyjnie)</div>

                      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8 }}>
                        Finalna struktura będzie liczona na serwerze przy <b>Start gry</b> (uwzględnia realną liczbę graczy).
                      </div>

                      <div style={{ overflow: "auto", maxHeight: 260 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ textAlign: "left", opacity: 0.9 }}>
                              <th style={{ padding: "6px 8px" }}>Lvl</th>
                              <th style={{ padding: "6px 8px" }}>SB</th>
                              <th style={{ padding: "6px 8px" }}>BB</th>
                              <th style={{ padding: "6px 8px" }}>Min</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.levels.map((lv) => (
                              <tr key={lv.level} style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}>
                                <td style={{ padding: "6px 8px" }}>{lv.level}</td>
                                <td style={{ padding: "6px 8px", color: "var(--color-accent)" }}>{lv.sb}</td>
                                <td style={{ padding: "6px 8px", color: "var(--color-accent)" }}>{lv.bb}</td>
                                <td style={{ padding: "6px 8px", opacity: 0.85 }}>{lv.minutes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}

              {/* Other settings */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
                    Max graczy {mode === "tournament" ? "(2–8)" : "(2–10)"}
                  </div>
                  <input
                    className={inputCls}
                    type="number"
                    min={2}
                    max={mode === "tournament" ? 8 : 10}
                    value={maxPlayers}
                    onChange={(e) => setMaxPlayers(Number(e.target.value))}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Czas na ruch (s)</div>
                  <input
                    className={inputCls}
                    type="number"
                    min={5}
                    max={300}
                    value={decisionTimeSec}
                    onChange={(e) => setDecisionTimeSec(Number(e.target.value))}
                  />
                </div>
              </div>

              <button className="btn btn-primary" type="button" onClick={onCreate}>
                Utwórz stół
              </button>

              {mode === "tournament" ? (
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                  Start blindy są na razie stałe: <b>25/50</b>. Później możemy dodać “BB start” do presetów.
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="panel-card">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 900 }}>
                Stół: {room.code} · status: {room.status}
              </div>

              <button className="btn btn-secondary" type="button" onClick={() => navigator.clipboard?.writeText(room.code)}>
                Kopiuj kod
              </button>

              {room.status === "running" ? (
                <button className="btn btn-primary" type="button" onClick={() => nav(`/pokergame/${room.code}`)}>
                  Wejdź na stół
                </button>
              ) : null}

              {isHost && room.status !== "running" ? (
                <button className="btn btn-primary" type="button" onClick={onStartGame}>
                  Start gry
                </button>
              ) : null}

              <button className="btn btn-secondary" type="button" onClick={onLeave} style={{ marginLeft: "auto" }}>
                Opuść stół
              </button>
            </div>

            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
              <div>
                Limit graczy: <b>{room.settings?.maxPlayers}</b> · czas na ruch: <b>{room.settings?.decisionTimeSec}s</b>
              </div>
              <div style={{ marginTop: 6 }}>
                Tryb: <b>{room.settings?.mode || "cash"}</b>
                {" · "}SB/BB: <b>{room.settings?.sb}/{room.settings?.bb}</b>
                {" · "}Start stack: <b>{room.settings?.startingStackBB} BB</b>
              </div>
            </div>

            <div style={{ marginTop: 14, fontWeight: 800 }}>Gracze ({room.players?.length || 0})</div>

            <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
              {(room.players || []).map((p) => (
                <div
                  key={p.userId}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.14)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>{p.name}</div>
                  <div style={{ fontSize: 12, opacity: 0.75 }}>{p.userId}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {err ? <div style={{ marginTop: 14, color: "var(--color-accent)" }}>⚠ {err}</div> : null}
      </div>
    </div>
  );
}
